import { ElementRef, Ref, useMemo } from 'react';
import { Selection } from 'react-aria-components';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Badge } from 'v5/design-system/Badge';
import {
  ListBox,
  ListBoxHeader,
  ListBoxItem,
  ListBoxItemContent
} from 'v5/design-system/ListBox';
import { CalloutCreateFormData } from 'v5/features/callouts/callout-create.types';
import {
  getCalloutTemplateIcon,
  getCalloutTemplateLabel
} from 'v5/features/callouts/callout-template.helpers';
import {
  buildMappingsFromTemplate,
  getTranslatedCalloutTemplates
} from 'v5/features/callouts/callout-templates';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';

export interface CalloutTemplateSelectionStepProps {
  form: UseFormReturn<CalloutCreateFormData>;
  listRef?: Ref<ElementRef<typeof ListBox>>;
}

export function CalloutTemplateSelectionStep({
  form,
  listRef
}: CalloutTemplateSelectionStepProps) {
  const { t } = useTranslation('callouts');
  const templates = getTranslatedCalloutTemplates(t);
  const selectedTemplateId = form.watch('templateId');

  const selectedKeys = useMemo(
    () => new Set([String(selectedTemplateId)]),
    [selectedTemplateId]
  );

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all' || keys.size === 0) {
      return;
    }
    const key = [...keys][0];
    if (key == null) {
      return;
    }
    const templateId = Number(key);
    const template = templates.find(item => item.id === templateId);
    if (!template) {
      return;
    }
    form.setValue('templateId', templateId, { shouldDirty: true });
    form.setValue('mappings', buildMappingsFromTemplate(template), {
      shouldDirty: true
    });
  };

  return (
    <div className="flex flex-col min-h-0 overflow-hidden rounded-lg border border-border-default">
      <ListBoxHeader>
        <div className="text-base text-fg-muted flex items-center gap-2">
          <Info className="size-4 shrink-0" aria-hidden="true" />
          <span>{t('CalloutDialog.templateInfo')}</span>
        </div>
      </ListBoxHeader>
      <ScrollShadowWrapper className="max-h-112 min-h-0 flex-1 overflow-y-auto">
        <ListBox
          ref={listRef}
          aria-label={t('CalloutDialog.template')}
          selectionMode="single"
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          className="divide-y divide-border-default"
        >
          {templates.map(template => {
            const label = getCalloutTemplateLabel(template);
            const fieldCount = template.template_data.mappings.length;

            return (
              <ListBoxItem
                key={template.id}
                id={String(template.id)}
                textValue={`${label}, ${template.description}, ${t('CalloutDialog.templateFieldCount', { count: fieldCount })}`}
              >
                <ListBoxItemContent
                  icon={getCalloutTemplateIcon(template)}
                  primary={label}
                  secondary={template.description}
                  extra={
                    <Badge variant="gray" tone="subtle" size="sm">
                      {t('CalloutDialog.templateFieldCount', {
                        count: fieldCount
                      })}
                    </Badge>
                  }
                />
              </ListBoxItem>
            );
          })}
        </ListBox>
      </ScrollShadowWrapper>
    </div>
  );
}
